import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/api/client";
import type { OfferKind, Placement, Platform } from "@/types";

interface Props {
  onClose: () => void;
  onCreated: (p: Placement) => void;
}

const PROPERTY_TYPES = ["apartment", "house", "cottage", "land", "commercial"] as const;
const DEAL_TYPES = ["sale", "rent", "daily_rent", "mortgage"] as const;

type PropertyType = (typeof PROPERTY_TYPES)[number];
type DealType = (typeof DEAL_TYPES)[number];
type Currency = "USD" | "GEL" | "EUR";

// Pull the platform + listing id out of a pasted URL so the agent doesn't
// have to fill them in twice. Conservative — leaves the field empty if the
// URL doesn't match a recognised pattern instead of guessing.
function parseListingUrl(
  raw: string,
): { platform: Platform | null; externalId: string | null } {
  if (!raw) return { platform: null, externalId: null };
  let host = "";
  let path = "";
  try {
    const u = new URL(raw);
    host = u.host.toLowerCase();
    path = u.pathname;
  } catch {
    return { platform: null, externalId: null };
  }
  // The listing id is an 8-digit auto-increment. It appears either right after
  // the URL segment (…/pr/12345678, …/udzravi-qoneba/12345678) OR at the END of
  // a slug (…/udzravi-qoneba/qiravdeba-...-didubeshi-36166402). Prefer the
  // trailing id so slug URLs work; fall back to the after-segment form.
  const clean = path.replace(/\/+$/, "");
  const trailingId = clean.match(/(\d{5,})$/)?.[1] ?? null;
  if (host.endsWith("myhome.ge")) {
    const direct = path.match(/\/(?:pr|udzravi-qoneba)\/(\d+)/)?.[1] ?? null;
    return { platform: "myhome", externalId: trailingId ?? direct };
  }
  if (host.endsWith("ss.ge")) {
    const direct = path.match(/\/(?:udzravi-qoneba|real-estate)\/(\d+)/)?.[1] ?? null;
    return { platform: "ss", externalId: trailingId ?? direct };
  }
  return { platform: null, externalId: null };
}

export function ManualPlacementModal({ onClose, onCreated }: Props) {
  const [platform, setPlatform] = useState<Platform>("myhome");
  const [publishedUrl, setPublishedUrl] = useState("");
  const [externalId, setExternalId] = useState("");
  const [externalIdEdited, setExternalIdEdited] = useState(false);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>("USD");
  const [rooms, setRooms] = useState("");
  const [areaSqm, setAreaSqm] = useState("");
  const [propertyType, setPropertyType] = useState<PropertyType | "">("");
  const [dealType, setDealType] = useState<DealType | "">("");
  // Combined "5/9" entry — split on submit. Empty / left side only / both
  // are all valid (mirrors the existing edit semantics).
  const [floorCombo, setFloorCombo] = useState("");
  const [district, setDistrict] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [coverPhotoUrl, setCoverPhotoUrl] = useState("");
  const [offerKind, setOfferKind] = useState<OfferKind | "">("");
  const [offerValue, setOfferValue] = useState("");
  const [notes, setNotes] = useState("");
  const [showMore, setShowMore] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [busy, onClose]);

  // Auto-detect platform + listing ID from the pasted URL. Only overwrite
  // the listing ID if the user hasn't typed in that field themselves —
  // otherwise we'd clobber a manual edit on every URL keystroke.
  const detected = useMemo(() => parseListingUrl(publishedUrl), [publishedUrl]);
  useEffect(() => {
    if (detected.platform && detected.platform !== platform) {
      setPlatform(detected.platform);
    }
    if (!externalIdEdited && detected.externalId) {
      setExternalId(detected.externalId);
    }
  }, [detected, externalIdEdited, platform]);

  function splitFloor(): { floor: number | null; total: number | null; bad: boolean } {
    const s = floorCombo.trim();
    if (!s) return { floor: null, total: null, bad: false };
    const parts = s.split("/").map((x) => x.trim());
    const parseNum = (x: string): number | null => {
      if (!x) return null;
      const n = Number(x);
      return Number.isFinite(n) ? n : NaN as unknown as null;
    };
    const f = parseNum(parts[0]);
    const t = parts.length > 1 ? parseNum(parts[1]) : null;
    if (Number.isNaN(f) || Number.isNaN(t)) return { floor: null, total: null, bad: true };
    return { floor: f, total: t, bad: false };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);

    const url = publishedUrl.trim();
    if (!url) {
      setErr("Listing URL is required");
      return;
    }
    const fl = splitFloor();
    if (fl.bad) {
      setErr("Floor must be a number — use \"5/9\" for floor 5 of 9.");
      return;
    }

    const body: Record<string, unknown> = {
      platform,
      published_url: url,
    };
    const setIfNum = (key: string, raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      const n = Number(trimmed);
      if (Number.isFinite(n)) body[key] = n;
    };
    const setIfStr = (key: string, raw: string) => {
      const trimmed = raw.trim();
      if (trimmed) body[key] = trimmed;
    };
    setIfStr("external_id", externalId);
    setIfStr("title", title);
    setIfNum("price", price);
    if (price.trim()) body.currency = currency;
    setIfNum("rooms", rooms);
    setIfNum("area_sqm", areaSqm);
    if (propertyType) body.property_type = propertyType;
    if (dealType) body.deal_type = dealType;
    if (fl.floor != null) body.floor = fl.floor;
    if (fl.total != null) body.total_floors = fl.total;
    setIfStr("district", district);
    setIfStr("owner_name", ownerName);
    setIfStr("owner_phone", ownerPhone);
    setIfStr("source_url", sourceUrl);
    setIfStr("cover_photo_url", coverPhotoUrl);
    if (offerKind) {
      const v = Number(offerValue.trim());
      if (!Number.isFinite(v) || v < 0) {
        setErr("Commission must be a non-negative number");
        return;
      }
      if (offerKind === "percent" && v > 100) {
        setErr("Percent must be 0–100");
        return;
      }
      body.offer_kind = offerKind;
      body.offer_value = v;
    }
    setIfStr("notes", notes);

    setBusy(true);
    try {
      const created = await api<Placement>("/api/placements", {
        method: "POST",
        json: body,
      });
      onCreated(created);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Failed to create placement",
      );
    } finally {
      setBusy(false);
    }
  }

  const fieldCls = "input w-full text-[13px] py-1.5";
  const tinyLabel = "text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={submit}
        className="card w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-100">Add manual placement</div>
            <div className="text-[11px] text-slate-500">
              For listings you posted yourself outside the publish pipeline.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="text-slate-500 hover:text-slate-200 p-1 -mr-1"
          >
            ✕
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto space-y-3">
          {/* URL drives platform + listing-id auto-detection. Place it first
              so paste-then-tab reaches title in two keystrokes. */}
          <div>
            <div className={tinyLabel}>Listing URL *</div>
            <div className="flex gap-1.5">
              <select
                className="input text-[13px] py-1.5 px-2 w-[110px] flex-shrink-0"
                value={platform}
                onChange={(e) => setPlatform(e.target.value as Platform)}
                disabled={busy}
                aria-label="Platform"
              >
                <option value="myhome">MyHome</option>
                <option value="ss">SS.ge</option>
              </select>
              <input
                type="url"
                className={fieldCls}
                placeholder="https://www.myhome.ge/ka/pr/12345/"
                value={publishedUrl}
                onChange={(e) => setPublishedUrl(e.target.value)}
                required
                autoFocus
                disabled={busy}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <div className={tinyLabel}>Title</div>
              <input
                type="text"
                className={fieldCls}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <div className={tinyLabel}>Listing ID</div>
              <input
                type="text"
                className={`${fieldCls} mono`}
                placeholder="12345"
                value={externalId}
                onChange={(e) => {
                  setExternalId(e.target.value);
                  setExternalIdEdited(true);
                }}
                disabled={busy}
              />
            </div>
          </div>

          {/* Price + currency live in one combined cell. Currency only
              matters when there's a price, so we hide the affordance via
              opacity rather than disable to keep the layout stable. */}
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <div className={tinyLabel}>Price</div>
              <div className="flex">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`${fieldCls} rounded-r-none tabular-nums`}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  disabled={busy}
                />
                <select
                  className="input text-[13px] py-1.5 px-2 rounded-l-none border-l-0 w-[72px]"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                  disabled={busy}
                  aria-label="Currency"
                >
                  <option value="USD">USD</option>
                  <option value="GEL">GEL</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
            <div>
              <div className={tinyLabel}>Rooms</div>
              <input
                type="number"
                min="0"
                max="99"
                className={`${fieldCls} tabular-nums`}
                value={rooms}
                onChange={(e) => setRooms(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <div className={tinyLabel}>Area m²</div>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`${fieldCls} tabular-nums`}
                value={areaSqm}
                onChange={(e) => setAreaSqm(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <div>
              <div className={tinyLabel}>Type</div>
              <select
                className={fieldCls}
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value as PropertyType | "")}
                disabled={busy}
              >
                <option value="">—</option>
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className={tinyLabel}>Deal</div>
              <select
                className={fieldCls}
                value={dealType}
                onChange={(e) => setDealType(e.target.value as DealType | "")}
                disabled={busy}
              >
                <option value="">—</option>
                <option value="sale">Sale</option>
                <option value="rent">Rent</option>
                <option value="daily_rent">Daily</option>
                <option value="mortgage">Mortgage</option>
              </select>
            </div>
            <div>
              <div className={tinyLabel}>Floor</div>
              <input
                type="text"
                inputMode="numeric"
                className={`${fieldCls} tabular-nums`}
                placeholder="5/9"
                title='e.g. "5/9" for floor 5 of 9'
                value={floorCombo}
                onChange={(e) => setFloorCombo(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <div className={tinyLabel}>District</div>
              <input
                type="text"
                className={fieldCls}
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          {/* Commission stays inline-but-compact; the most common case is
              "no commission set" → both controls render but stay quiet. */}
          <div>
            <div className={tinyLabel}>Commission</div>
            <div className="flex gap-1.5">
              <select
                className="input text-[13px] py-1.5 px-2 w-[88px] flex-shrink-0"
                value={offerKind}
                onChange={(e) => setOfferKind(e.target.value as OfferKind | "")}
                disabled={busy}
              >
                <option value="">None</option>
                <option value="percent">Percent</option>
                <option value="fixed">
                  Fixed {currency === "USD" ? "$" : currency === "GEL" ? "₾" : currency}
                </option>
              </select>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`${fieldCls} tabular-nums`}
                placeholder={offerKind === "percent" ? "2.5" : offerKind === "fixed" ? "500" : "—"}
                value={offerValue}
                onChange={(e) => setOfferValue(e.target.value)}
                disabled={busy || !offerKind}
              />
            </div>
          </div>

          <div>
            <div className={tinyLabel}>Notes</div>
            <textarea
              className={`${fieldCls} min-h-[60px]`}
              placeholder="Owner availability, negotiation state, follow-up…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
              maxLength={4000}
            />
          </div>

          {/* Progressive-disclosure: owner + source + cover photo are
              rarely set on manual entries (they're easier to PATCH later
              from the row). Hidden by default to keep the modal one
              screen tall on a 13" laptop. */}
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
            aria-expanded={showMore}
          >
            <span
              className={`inline-block transition-transform ${showMore ? "rotate-90" : ""}`}
              aria-hidden
            >
              ▸
            </span>
            More details (owner, source, cover)
          </button>

          {showMore && (
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <div className={tinyLabel}>Owner name</div>
                  <input
                    type="text"
                    className={fieldCls}
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    disabled={busy}
                  />
                </div>
                <div>
                  <div className={tinyLabel}>Owner phone</div>
                  <input
                    type="tel"
                    className={`${fieldCls} mono`}
                    placeholder="+995 5xx …"
                    value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>
              <div>
                <div className={tinyLabel}>Source URL — original we reuploaded from</div>
                <input
                  type="url"
                  className={fieldCls}
                  placeholder="https://www.myhome.ge/ka/pr/00000/"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div>
                <div className={tinyLabel}>Cover photo URL</div>
                <input
                  type="url"
                  className={fieldCls}
                  value={coverPhotoUrl}
                  onChange={(e) => setCoverPhotoUrl(e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>
          )}

          {err && (
            <div className="text-[12px] text-rose-300 bg-rose-500/10 ring-1 ring-rose-400/20 rounded px-3 py-1.5">
              {err}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-white/5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-secondary text-[13px] py-1.5"
          >
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-primary text-[13px] py-1.5">
            {busy ? "Saving…" : "Add placement"}
          </button>
        </div>
      </form>
    </div>
  );
}
